// src/contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

// Create context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Set up the initial auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if user is authenticated
        if (authService.isAuthenticated()) {
          setCurrentUser(authService.getUser());
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (username, password) => {
    setError('');
    try {
      const result = await authService.login(username, password);
      setCurrentUser(authService.getUser());
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Logout function
  const logout = async (redirectCallback) => {
    setError('');
    try {
      await authService.logout();
      setCurrentUser(null);
      
      // Use callback for navigation if provided
      if (typeof redirectCallback === 'function') {
        redirectCallback();
      }
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    setError('');
    try {
      return await authService.changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    setError('');
    try {
      return await authService.forgotPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Reset password function
  const resetPassword = async (token, email, newPassword) => {
    setError('');
    try {
      return await authService.resetPassword(token, email, newPassword);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    changePassword,
    forgotPassword,
    resetPassword,
    isAuthenticated: () => authService.isAuthenticated()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;